(() => {
  'use strict';

  const config = window.HAPSLOK || {};
  const routes = config.routes || {};
  const strings = config.strings || {};

  const announce = (message) => {
    const region = document.querySelector('[data-live-region]');
    if (region) region.textContent = message;
  };

  const focusable = (root) =>
    root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');

  const refreshCart = async () => {
    const [cartResponse, htmlResponse] = await Promise.all([
      fetch(`${routes.cart}.js`),
      fetch(`${routes.cart}?section_id=cart-items`)
    ]);

    if (cartResponse.ok) {
      const cart = await cartResponse.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    }

    if (!htmlResponse.ok) return;
    const parsed = new DOMParser().parseFromString(await htmlResponse.text(), 'text/html');
    [
      ['[data-cart-lines]', 'innerHTML'],
      ['[data-cart-footer]', 'innerHTML']
    ].forEach(([selector]) => {
      const fresh = parsed.querySelector(selector);
      if (!fresh) return;
      document.querySelectorAll(selector).forEach((node) => {
        node.innerHTML = fresh.innerHTML;
      });
    });
  };

  const reveal = () => {
    const targets = document.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px' });

    targets.forEach((el) => observer.observe(el));
  };

  document.addEventListener('DOMContentLoaded', reveal);
  document.addEventListener('shopify:section:load', reveal);

  const initHero = (root = document) => {
    root.querySelectorAll('[data-hero]').forEach((hero) => {
      window.requestAnimationFrame(() => hero.classList.add('is-ready'));
    });
  };

  document.addEventListener('DOMContentLoaded', () => initHero());
  document.addEventListener('shopify:section:load', (event) => initHero(event.target));

  const bindHeroScroll = () => {
    const hero = document.querySelector('[data-hero]');
    if (!hero || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let ticking = false;
    const update = () => {
      const progress = Math.min(1, Math.max(0, window.scrollY / Math.max(hero.offsetHeight, 1)));
      hero.style.setProperty('--hero-progress', progress.toFixed(3));
      ticking = false;
    };
    const request = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
  };

  document.addEventListener('DOMContentLoaded', bindHeroScroll);

  const bindDemoEngraving = (root = document) => {
    root.querySelectorAll('[data-demo-name]').forEach((input) => {
      if (input.dataset.boundDemoName) return;
      input.dataset.boundDemoName = 'true';
      input.addEventListener('input', () => {
        const preview = input.closest('section')?.querySelector('[data-demo-preview]');
        const value = input.value.trim();
        if (preview) {
          preview.textContent = value ? value.toLocaleUpperCase('nl-NL') : '';
          preview.classList.toggle('has-value', Boolean(value));
        }
      });
      input.dispatchEvent(new Event('input'));
    });
  };

  document.addEventListener('DOMContentLoaded', () => bindDemoEngraving());
  document.addEventListener('shopify:section:load', (event) => bindDemoEngraving(event.target));

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      this.querySelector('.header')?.classList.toggle('is-stuck', window.scrollY > 8);
    }
  }
  customElements.define('site-header', SiteHeader);

  class ColorStory extends HTMLElement {
    connectedCallback() {
      this.tiles = [...this.querySelectorAll('[data-color-tile]')];
      this.tiles.forEach((tile) => {
        tile.addEventListener('mouseenter', () => this.preview(tile.dataset.tone));
        tile.addEventListener('focus', () => this.preview(tile.dataset.tone));
        tile.addEventListener('click', () => this.select(tile));
        tile.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
          const current = this.tiles.indexOf(tile);
          const next = this.tiles[(current + direction + this.tiles.length) % this.tiles.length];
          next.focus();
          this.select(next);
        });
      });
    }

    preview(tone) {
      if (tone) this.dataset.activeTone = tone;
    }

    select(tile) {
      this.tiles.forEach((item) => item.setAttribute('aria-checked', String(item === tile)));
      this.preview(tile.dataset.tone);
    }
  }
  customElements.define('color-story', ColorStory);

  class StickerStory extends HTMLElement {
    connectedCallback() {
      this.featured = this.querySelector('[data-sticker-featured]');
      this.cards = [...this.querySelectorAll('[data-sticker-card]')];
      if (!this.featured || this.cards.length === 0) return;

      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.select(card));
      });

      this.select(this.cards.find((card) => card.getAttribute('aria-pressed') === 'true') || this.cards[0]);
    }

    select(card) {
      const src = card.dataset.stickerSrc;
      const title = card.dataset.stickerTitle || card.querySelector('.display-sm')?.textContent?.trim() || 'HAPSLOK';
      if (!src) return;

      this.featured.src = src;
      this.featured.alt = `${title} stickerpack preview`;
      this.cards.forEach((item) => item.setAttribute('aria-pressed', String(item === card)));
    }
  }
  customElements.define('sticker-story', StickerStory);

  /* ------------------------------------------------------------------------
     HAPSLOK Studio
     Laat de bezoeker de set samenstellen voordat hij naar de productpagina
     gaat. Verkoopt zelf niets: de keuzes reizen als URL-parameters mee en
     worden daar in het echte productformulier gezet.
     ------------------------------------------------------------------------ */

  class SetStudio extends HTMLElement {
    connectedCallback() {
      this.scene = this.querySelector('[data-studio-scene]');
      this.engravings = [...this.querySelectorAll('[data-engrave]')];
      this.nameInput = this.querySelector('[data-studio-name]');
      this.counter = this.querySelector('[data-studio-count]');
      this.status = this.querySelector('[data-studio-status]');
      this.cta = this.querySelector('[data-studio-cta]');

      this.baseHref = this.cta ? this.cta.getAttribute('href') : '';
      this.maxLength = Number(this.dataset.nameMax) || 12;
      this.name = '';
      this.choice = { color: '', pack: '' };

      this.groups = [...this.querySelectorAll('[data-studio-group]')];
      this.groups.forEach((group) => this.connectGroup(group));

      this.nameInput?.addEventListener('input', () => this.syncName());

      // De gravure schaalt mee met de scène, dus we meten opnieuw bij resize.
      if ('ResizeObserver' in window && this.scene) {
        this.resizeObserver = new ResizeObserver(() => this.fitEngravings());
        this.resizeObserver.observe(this.scene);
      }

      this.syncName();

      // Webfonts veranderen de letterbreedte, dus meten we opnieuw zodra ze staan.
      document.fonts?.ready.then(() => this.fitEngravings());
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    /* --- Keuzegroepen ----------------------------------------------------- */

    connectGroup(group) {
      const options = this.optionsOf(group);
      if (!options.length) return;

      options.forEach((option) =>
        option.addEventListener('click', () => this.select(group, option))
      );

      group.addEventListener('keydown', (event) => this.onGroupKeydown(event, group));

      const checked = options.find((o) => o.getAttribute('aria-checked') === 'true');
      this.select(group, checked || options[0], { silent: true });
    }

    optionsOf(group) {
      return [...group.querySelectorAll('[data-studio-option]')];
    }

    /* Pijltjestoetsen horen bij een radiogroep; Tab springt naar de volgende
       groep in plaats van door alle opties heen. */
    onGroupKeydown(event, group) {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      const options = this.optionsOf(group);
      const current = options.indexOf(document.activeElement);
      if (current < 0) return;

      event.preventDefault();

      let next;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = options.length - 1;
      else {
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        next = (current + step + options.length) % options.length;
      }

      this.select(group, options[next]);
    }

    select(group, option, { silent = false } = {}) {
      if (!option) return;

      this.optionsOf(group).forEach((item) => {
        const active = item === option;
        item.setAttribute('aria-checked', String(active));
        item.tabIndex = active ? 0 : -1;
      });

      const kind = group.dataset.studioGroup;
      this.choice[kind] = option.dataset.label || '';

      if (kind === 'color') {
        this.style.setProperty('--studio-surface', option.dataset.surface);
        this.style.setProperty('--studio-shade', option.dataset.shade);
      } else if (kind === 'pack') {
        const image = option.dataset.image;
        this.style.setProperty('--studio-pack', image ? `url("${image}")` : 'none');
        this.style.setProperty('--studio-pack-position', option.dataset.position || 'center');
      }

      if (!silent) {
        option.focus();
        this.announce();
      }

      this.updateLink();
    }

    /* --- Naam ------------------------------------------------------------- */

    syncName() {
      if (this.nameInput) {
        const upper = this.nameInput.value.toLocaleUpperCase('nl-NL');
        if (upper !== this.nameInput.value) {
          const caret = this.nameInput.selectionStart;
          this.nameInput.value = upper;
          this.nameInput.setSelectionRange(caret, caret);
        }
        this.name = upper.trim();
        if (this.counter) this.counter.textContent = `${upper.length}/${this.maxLength}`;
      }

      this.engravings.forEach((engraving) => {
        engraving.textContent = this.name;
      });

      this.fitEngravings();
      this.updateLink();
    }

    /**
     * Zet de lettergrootte van de gravure zo dat een naam altijd binnen zijn
     * venster past. CSS kan de breedte van een woord niet meten, dus dat
     * gebeurt hier: eerst een schatting, daarna één correctie op de echte maat.
     */
    fitEngravings() {
      this.engravings.forEach((engraving) => {
        const frame = engraving.parentElement;
        if (!frame) return;

        const vertical = engraving.dataset.axis === 'y';
        const along = vertical ? frame.clientHeight : frame.clientWidth;
        const across = vertical ? frame.clientWidth : frame.clientHeight;
        const text = engraving.textContent || engraving.dataset.empty || '';

        if (!along || !across || !text.length) return;

        // 0.72 benadert de gemiddelde letterbreedte inclusief spatiëring.
        let size = Math.min(across * 0.92, along / (text.length * 0.72));
        engraving.style.fontSize = `${Math.max(8, size)}px`;

        const overshoot = vertical
          ? engraving.scrollHeight - along
          : engraving.scrollWidth - along;

        if (overshoot > 0) {
          size *= along / (along + overshoot);
          engraving.style.fontSize = `${Math.max(8, size)}px`;
        }
      });
    }

    /* --- Overdracht naar de productpagina --------------------------------- */

    updateLink() {
      if (this.dataset.handoff !== 'true' || !this.cta || !this.baseHref) return;

      const url = new URL(this.baseHref, window.location.origin);
      const apply = (key, value) => {
        if (value) url.searchParams.set(key, value);
        else url.searchParams.delete(key);
      };

      apply('kleur', this.choice.color);
      apply('stickerpack', this.choice.pack);
      apply('naam', this.name);

      // Wijst de knop naar een ander domein, dan laten we de hele URL staan.
      const sameOrigin = url.origin === window.location.origin;
      this.cta.setAttribute('href', sameOrigin ? `${url.pathname}${url.search}` : url.toString());
    }

    announce() {
      if (!this.status) return;
      const parts = [this.choice.color, this.choice.pack].filter(Boolean);
      this.status.textContent = parts.join(' · ');
    }
  }
  customElements.define('set-studio', SetStudio);

  /**
   * Vult op de productpagina de keuzes in die in de studio zijn gemaakt.
   * Werkt op de bestaande formuliervelden, zodat er maar één plek is waar
   * een bestelling ontstaat.
   */
  const applyStudioChoices = () => {
    const params = new URLSearchParams(window.location.search);
    const colour = params.get('kleur');
    const pack = params.get('stickerpack');
    const name = params.get('naam');
    if (!colour && !pack && !name) return;

    const check = (input) => {
      if (!input || input.disabled || input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const findRadio = (selector, value) => {
      if (!value) return null;
      const wanted = value.trim().toLowerCase();
      return [...document.querySelectorAll(selector)].find(
        (input) => input.value.trim().toLowerCase() === wanted
      );
    };

    check(findRadio('variant-picker input[type="radio"]', colour));
    check(findRadio('input[name="properties[Stickerpack]"]', pack));

    if (!name) return;

    const engraving = document.querySelector('name-engraving');
    if (!engraving) return;

    const toggles = [...engraving.querySelectorAll('[data-engrave-toggle]')];
    check(toggles.find((toggle) => toggle.value === engraving.dataset.yesValue));

    const field = engraving.querySelector('[data-name-input]');
    if (!field) return;

    field.value = name.slice(0, Number(engraving.dataset.maxLength) || 12);
    field.dispatchEvent(new Event('input', { bubbles: true }));
  };

  class Panel extends HTMLElement {
    connectedCallback() {
      this.onKeydown = this.onKeydown.bind(this);
      document.querySelectorAll(this.dataset.opener || '').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(button);
        });
      });
      this.querySelectorAll('[data-panel-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      document.addEventListener('keydown', this.onKeydown);
      window.requestAnimationFrame(() => focusable(this)[0]?.focus());
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      document.removeEventListener('keydown', this.onKeydown);
      this.opener?.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = [...focusable(this)];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('nav-drawer', class extends Panel {});
  customElements.define('cart-drawer', class extends Panel {});

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.dataset.step === 'up' || button.dataset.action === 'increase' ? 1 : -1;
          const min = Number(this.input.min || 0);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const next = Math.min(max, Math.max(min, Number(this.input.value || min) + step));
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variant-data]');
      if (!data) return;
      this.variants = JSON.parse(data.textContent);
      this.form = document.getElementById(this.dataset.form);
      this.idInput = this.form?.querySelector('[name="id"]');
      this.addEventListener('change', () => this.update());
      this.update(true);
    }

    get selected() {
      return [...this.querySelectorAll('.option-group')].map((group) => group.querySelector('input[type="radio"]:checked')?.value);
    }

    update(initial = false) {
      const selected = this.selected;
      const match = this.variants.find((variant) => variant.options.every((option, index) => option === selected[index]));

      this.querySelectorAll('[data-selected-option]').forEach((el) => {
        el.textContent = selected[Number(el.dataset.selectedOption)] || '';
      });

      this.querySelectorAll('.option-group').forEach((group, groupIndex) => {
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const candidate = [...selected];
          candidate[groupIndex] = input.value;
          const variant = this.variants.find((v) => v.options.every((option, index) => option === candidate[index]));
          input.disabled = !variant || !variant.available;
        });
      });

      const button = this.form?.querySelector('[data-add-button]');
      if (!match) {
        if (button) button.disabled = true;
        return;
      }

      if (this.idInput) this.idInput.value = match.id;
      if (!initial) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        history.replaceState({}, '', url);
      }

      const productLayout = this.closest('.product-layout');
      const price = productLayout?.querySelector('[data-variant-price]') || document.querySelector('[data-variant-price]');
      if (price && match.price_html) price.innerHTML = match.price_html;
      document.querySelectorAll('[data-sticky-price]').forEach((el) => { el.innerHTML = match.price_html; });

      if (button) {
        button.disabled = !match.available;
        const label = button.querySelector('[data-add-label]') || button;
        label.textContent = match.available ? button.dataset.labelDefault : strings.soldOut;
      }

      if (match.featured_media_id) document.querySelector('product-gallery')?.showMedia(match.featured_media_id);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
      if (!this.track) return;
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.scrollToIndex(index)));
      this.track.addEventListener('scroll', () => this.syncDots(), { passive: true });
    }

    get slides() {
      return [...this.track.children];
    }

    syncDots() {
      const index = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }

    scrollToIndex(index) {
      const slide = this.slides[index];
      if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }

    showMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      if (matchMedia('(min-width: 750px)').matches) {
        this.slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        this.scrollToIndex(index);
      }
    }
  }
  customElements.define('product-gallery', ProductGallery);

  class NameEngraving extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-name-input]');
      this.preview = this.querySelector('[data-name-preview]');
      this.counter = this.querySelector('[data-name-count]');
      this.error = this.querySelector('[data-name-error]');
      this.panel = this.querySelector('[data-name-panel]');
      this.toggles = [...this.querySelectorAll('[data-engrave-toggle]')];
      this.maxLength = Number(this.dataset.maxLength || 12);
      this.uppercase = this.dataset.uppercase === 'true';
      this.pattern = /^[\p{L}\p{M} '’-]+$/u;

      this.toggles.forEach((toggle) => toggle.addEventListener('change', () => this.syncPanel()));
      this.input?.addEventListener('input', () => this.onInput());
      this.syncPanel();
      this.onInput();
    }

    get wantsEngraving() {
      if (!this.toggles.length) return true;
      return this.toggles.find((toggle) => toggle.checked)?.value === this.dataset.yesValue;
    }

    syncPanel() {
      const wants = this.wantsEngraving;
      if (this.panel) this.panel.hidden = !wants;
      if (this.input) this.input.disabled = !wants;
      if (!wants) this.setError('');
    }

    onInput() {
      if (!this.input) return;
      if (this.uppercase) {
        const start = this.input.selectionStart;
        this.input.value = this.input.value.toLocaleUpperCase('nl-NL');
        this.input.setSelectionRange(start, start);
      }
      const value = this.input.value;
      if (this.preview) this.preview.textContent = value;
      if (this.counter) this.counter.textContent = `${value.length}/${this.maxLength}`;
      if (value) this.setError('');
    }

    setError(message) {
      if (this.error) this.error.textContent = message;
      this.input?.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    validate() {
      if (!this.wantsEngraving || !this.input) return true;
      const value = this.input.value.trim();
      if (!value) {
        this.setError(strings.nameRequired || 'Vul een naam in.');
        this.input.focus();
        return false;
      }
      if (value.length > this.maxLength || !this.pattern.test(value)) {
        this.setError(strings.nameInvalid || 'Gebruik alleen letters, spaties, streepjes en apostrofs.');
        this.input.focus();
        return false;
      }
      this.input.value = value;
      this.setError('');
      return true;
    }
  }
  customElements.define('name-engraving', NameEngraving);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.form?.querySelector('[data-add-button]');
      this.label = this.form?.querySelector('[data-add-label]') || this.button;
      this.error = this.querySelector('[data-form-error]');
      this.form?.addEventListener('submit', (event) => this.submit(event));
    }

    submit(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled) return;
      const engraving = this.form.closest('.product-info')?.querySelector('name-engraving') || document.querySelector('name-engraving');
      if (engraving && !engraving.validate()) return;
      this.add();
    }

    async add() {
      const original = this.label.textContent;
      this.button.disabled = true;
      this.label.textContent = '...';
      if (this.error) this.error.textContent = '';

      try {
        const response = await fetch(routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const result = await response.json();
        if (result.status) throw new Error(result.description || result.message);
        await refreshCart();
        announce(strings.addedToCart || 'Toegevoegd');
        if (config.cartDrawer) document.querySelector('cart-drawer')?.open();
        else window.location.href = routes.cart;
      } catch (error) {
        if (this.error) this.error.textContent = error.message;
      } finally {
        this.button.disabled = false;
        this.label.textContent = original;
      }
    }
  }
  customElements.define('product-form', ProductForm);

  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector(this.dataset.watch);
      this.querySelector('[data-sticky-add]')?.addEventListener('click', () => document.querySelector('product-form form')?.requestSubmit());
      if (!anchor || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      }, { rootMargin: '0px 0px -90px 0px' }).observe(anchor);
    }
  }
  customElements.define('sticky-buy', StickyBuy);

  class CartLines extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-line-quantity]');
        if (input) this.change(input.dataset.line, input.value);
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-line-remove]');
        if (!remove) return;
        event.preventDefault();
        this.change(remove.dataset.line, 0);
      });
    }

    async change(line, quantity) {
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
        });
        if (!response.ok) throw new Error(response.statusText);
        await refreshCart();
        if (Number(quantity) === 0) announce(strings.removed || 'Verwijderd');
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('cart-lines', CartLines);

  // Alle custom elements zijn nu geregistreerd en dus opgewaardeerd; pas
  // daarna kunnen we de keuzes uit de studio in het productformulier zetten.
  applyStudioChoices();
})();
